import React, { FC, useEffect, useRef } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { Flex } from "antd";
import { format } from "date-fns";
import { useForm } from "react-hook-form/dist/index.ie11";
import { useTranslation } from "react-i18next";

import { LANG } from "~/constans/commom";
import { useGetLocationData, useSetLocationData } from "~/hooks/locationData";
import {
  QuartilesRatingVersion,
  useSetGrVersionMutation,
  useSetQVersionMutation,
} from "~/schemas/generated/graphql";
import { dateFormat, validateDate } from "~/shared/lib/DateFormat/dateFormat";
import { Field } from "~/shared/ui/Field";

import { getYupSchemaForRatingVersionForm } from "./lib";
import { VERSION_QUARTERS } from "./options";

import s from "./RatingVersionForm.less";

interface IProps {
  setCloseModal: () => void;
  refetch;
  type?: string;
  ratingVersion?: QuartilesRatingVersion;
  formId: string;
}

const RatingVersionForm: FC<IProps> = ({
  setCloseModal,
  refetch,
  type = "",
  ratingVersion = {},
  formId,
}) => {
  const { t } = useTranslation();
  const isEn = localStorage.getItem("language") === LANG.en;
  const setData = useSetLocationData();
  const { openChildModal } = useGetLocationData();
  const isFirstSelectQuarter = useRef(false);

  const [setGrVersionMutation] = useSetGrVersionMutation({
    onCompleted() {
      refetch();
      setCloseModal();
    },
  });
  const [setQVersionMutation] = useSetQVersionMutation({
    onCompleted() {
      refetch();
      setCloseModal();
    },
  });
  const { id, quarter, year, dateBegin, IndicatorId, IndicatorGroupId } =
    ratingVersion;

  const { handleSubmit, control, watch, setValue, errors } = useForm({
    defaultValues: {
      quarter: quarter
        ? { label: quarter, value: quarter }
        : { label: "1", value: 1 },
      year: year || Number(format(new Date(), "yyyy")),
      dateBegin: validateDate(dateBegin)
        ? new Date(dateBegin)
        : new Date(`${format(new Date(), "yyyy")}-01-01`),
      IndicatorId,
      IndicatorGroupId,
    },
    resolver: yupResolver(getYupSchemaForRatingVersionForm(t)),
  });
  const selectedQuarter: { value?: number } = watch("quarter") || {};
  const selectedYear =
    String(watch("year")).length === 4 ? watch("year") : undefined;
  const selectedDateBegin = watch("dateBegin");

  const optionsType = {
    GR: "GR",
    Q: "Q",
  };

  const quarterOptions = VERSION_QUARTERS.map((option) => ({
    label: String(option),
    value: option,
  }));

  const onSubmit = (data) => {
    const variables = {
      ...data,
      id,
      quarter: +data.quarter.value ? +data.quarter.value : quarter,
      year: +data.year,
    };

    type === optionsType.GR &&
      setGrVersionMutation({
        variables,
      });
    type === optionsType.Q &&
      setQVersionMutation({
        variables,
      });
  };

  useEffect(() => {
    const datenews = selectedYear || year || Number(format(new Date(), "yyyy"));

    isFirstSelectQuarter.current &&
      selectedQuarter.value === 1 &&
      setValue("dateBegin", new Date(`${datenews}-01-01`));

    isFirstSelectQuarter.current &&
      selectedQuarter.value === 2 &&
      setValue("dateBegin", new Date(`${datenews}-04-01`));

    isFirstSelectQuarter.current &&
      selectedQuarter.value === 3 &&
      setValue("dateBegin", new Date(`${datenews}-07-01`));

    isFirstSelectQuarter.current &&
      selectedQuarter.value === 4 &&
      setValue("dateBegin", new Date(`${datenews}-10-01`));

    isFirstSelectQuarter.current = !!quarter;
  }, [selectedYear, year, dateBegin, selectedQuarter.value]);

  useEffect(() => {
    if (dateBegin) {
      const datenews =
        selectedYear || year || Number(format(new Date(), "yyyy"));

      const auf = dateFormat(new Date(dateBegin), { type: "monthDay" });
      selectedYear !== year &&
        setValue("dateBegin", new Date(`${datenews}-${auf}`));
    }
  }, [selectedYear, year, dateBegin]);

  useEffect(() => {
    const currentQuarter = selectedQuarter.value || quarter || 1;
    const currentYear =
      selectedYear || year || Number(format(new Date(), "yyyy"));
    const currentDateBegin = selectedDateBegin || dateBegin;
    const setError = () =>
      setData(
        "openChildModal",
        isEn
          ? `Effective date outside the period: "${currentQuarter} quarter ${currentYear}"`
          : `Дата введения в действие вне периода: "${currentQuarter} квартал ${currentYear}"`,
      );
    const setErrorUncklow = () => setData("openChildModal", "");

    currentQuarter === 1 &&
      currentDateBegin &&
      (currentDateBegin < new Date(`${currentYear}-01-01`)
        ? setError()
        : currentDateBegin > new Date(`${currentYear}-03-31`)
          ? setError()
          : setErrorUncklow());

    currentQuarter === 2 &&
      currentDateBegin &&
      (currentDateBegin < new Date(`${currentYear}-04-01`)
        ? setError()
        : currentDateBegin > new Date(`${currentYear}-06-30`)
          ? setError()
          : setErrorUncklow());

    currentQuarter === 3 &&
      currentDateBegin &&
      (currentDateBegin < new Date(`${currentYear}-07-01`)
        ? setError()
        : currentDateBegin > new Date(`${currentYear}-09-30`)
          ? setError()
          : setErrorUncklow());

    currentQuarter === 4 &&
      currentDateBegin &&
      (currentDateBegin < new Date(`${currentYear}-10-01`)
        ? setError()
        : currentDateBegin > new Date(`${currentYear}-12-31`)
          ? setError()
          : setErrorUncklow());
  }, [
    quarter,
    selectedDateBegin,
    selectedQuarter.value,
    year,
    dateBegin,
    openChildModal,
  ]);

  return (
    <form id={formId} onSubmit={handleSubmit(onSubmit)}>
      <Flex vertical gap={10}>
        <Field
          type="select"
          name="quarter"
          control={control}
          label={t("editor.versions.quarter")}
          options={quarterOptions}
          labelInValue
        />
        <Field
          type="input"
          name="year"
          control={control}
          label={t("editor.versions.year")}
          error={errors["year"]?.message}
        />
        <Field
          type="datePicker"
          name="dateBegin"
          control={control}
          label={t("editor.versions.dateBegin")}
          format="DD.MM.YYYY"
        />
        {openChildModal && <div className={s.errorDate}>{openChildModal}</div>}
        <Field
          type="input"
          name="IndicatorId"
          control={control}
          label={
            type === optionsType.Q
              ? t("editor.versions.IndicatorId")
              : t("editor.versions.IndicatorGR")
          }
          error={errors["IndicatorId"]?.message}
        />
        {type === "Q" && (
          <Field
            type="input"
            name="IndicatorGroupId"
            control={control}
            label={t("editor.versions.IndicatorGroupId")}
            error={errors["IndicatorGroupId"]?.message}
          />
        )}
      </Flex>
    </form>
  );
};
export default RatingVersionForm;
